import { useMemo } from "react";
import { formatMoney } from "@/lib/format";
import { Check, X } from "lucide-react";

const COLORS = ["#FF5A36", "#FFC400", "#29C7E8", "#FF4D9D", "#7CE645"];

export default function GoalTimeline({ goals, balance, onDeleteGoal }) {
  // Sort goals by price ascending
  const sortedGoals = useMemo(
    () => [...goals].sort((a, b) => a.price - b.price),
    [goals]
  );

  // Max scale - whichever is larger between most expensive goal and balance, plus headroom
  const maxGoalPrice = sortedGoals.length > 0 ? sortedGoals[sortedGoals.length - 1].price : 100;
  const max = Math.max(maxGoalPrice, balance) * 1.15;
  const safeMax = max > 0 ? max : 100;

  const progressPct = Math.min(100, (balance / safeMax) * 100);

  // The track is inset from the card edges so $0 label and labels at the right have room.
  const TRACK_INSET = "32px"; // pixels inset on each side

  return (
    <div
      data-testid="goal-timeline"
      className="chunky-card p-5 sm:p-6 mt-5"
      style={{ backgroundColor: "#FFFDF6" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl sm:text-3xl text-navy">
          MY GOALS
        </h2>
        <span className="font-display text-base text-navy/60">
          {sortedGoals.length} goal{sortedGoals.length === 1 ? "" : "s"}
        </span>
      </div>

      {sortedGoals.length === 0 ? (
        <div className="py-8 text-center font-display text-navy/60">
          Add a goal below to see it on your timeline! 🎯
        </div>
      ) : (
        <div className="relative" style={{ paddingTop: 96, paddingBottom: 56 }}>
          {/* Inset wrapper so $0 anchor and end labels have breathing room */}
          <div
            className="relative"
            style={{ marginLeft: TRACK_INSET, marginRight: TRACK_INSET }}
          >
            {/* === ABOVE BAR: name + icon for each goal === */}
            {sortedGoals.map((goal, idx) => {
              const pct = Math.min(100, (goal.price / safeMax) * 100);
              const reached = balance >= goal.price;
              const color = COLORS[idx % COLORS.length];
              return (
                <div
                  key={`top-${goal.id}`}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: `${pct}%`,
                    transform: "translateX(-50%)",
                    bottom: "32px", // sits just above the track (track height ~16px + small gap)
                    width: 96,
                    marginLeft: -48,
                  }}
                >
                  <div
                    className="font-display text-[11px] sm:text-xs text-navy leading-tight text-center mb-1 px-1"
                    title={goal.name}
                  >
                    {goal.name}
                  </div>
                  <div
                    data-testid={`goal-dot-${goal.id}`}
                    className={`relative rounded-full border-[3px] border-navy flex items-center justify-center ${reached ? "goal-reached" : ""}`}
                    style={{
                      width: 48,
                      height: 48,
                      boxShadow: "0 4px 0 0 #2A2D7C",
                      backgroundColor: reached ? "#E8FFD8" : "white",
                    }}
                  >
                    {goal.image_url ? (
                      <img
                        src={goal.image_url}
                        alt={goal.name}
                        className="w-9 h-9 object-contain goal-wobble"
                        style={{ animationDelay: `${idx * 0.3}s` }}
                      />
                    ) : (
                      <div
                        className="w-full h-full rounded-full flex items-center justify-center font-display text-base text-white"
                        style={{ backgroundColor: color }}
                      >
                        {goal.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {reached && (
                      <div
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full border-2 border-navy flex items-center justify-center"
                        style={{ backgroundColor: "#7CE645" }}
                      >
                        <Check className="w-3 h-3 text-navy" strokeWidth={4} />
                      </div>
                    )}
                    <button
                      data-testid={`delete-goal-${goal.id}`}
                      onClick={() => onDeleteGoal(goal.id, goal.name)}
                      className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full border-2 border-navy flex items-center justify-center hover:scale-110 transition-transform"
                      style={{ backgroundColor: "#FF4D9D" }}
                      aria-label={`Delete ${goal.name}`}
                    >
                      <X className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                    </button>
                  </div>
                  {/* connector tick from icon to bar */}
                  <div
                    className="w-1 bg-navy"
                    style={{ height: 10, marginTop: -1 }}
                  />
                </div>
              );
            })}

            {/* === BAR/TRACK === */}
            <div className="relative">
              {/* Track */}
              <div
                className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-4 rounded-full border-2 border-navy"
                style={{ backgroundColor: "#FFE9C2" }}
              />
              {/* Progress fill */}
              <div
                data-testid="progress-fill"
                className="progress-fill absolute left-0 top-1/2 -translate-y-1/2 h-4 rounded-full border-2 border-navy transition-all duration-700 ease-out"
                style={{ width: `${progressPct}%`, minWidth: progressPct > 0 ? "16px" : 0 }}
              />

              {/* Balance "YOU: $X" marker above end of fill */}
              <div
                data-testid="balance-marker"
                className="absolute -top-[78px] transition-all duration-700 ease-out z-10"
                style={{
                  left: `${progressPct}%`,
                  transform: "translateX(-50%)",
                }}
              >
                <div
                  className="font-display text-xs sm:text-sm text-white px-2.5 py-1 rounded-full border-2 border-navy whitespace-nowrap"
                  style={{ backgroundColor: "#FF5A36", boxShadow: "0 3px 0 0 #2A2D7C" }}
                >
                  YOU: {formatMoney(balance)}
                </div>
                <div
                  className="w-0 h-0 mx-auto"
                  style={{
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderTop: "8px solid #2A2D7C",
                  }}
                />
              </div>

              {/* $0 anchor on the left */}
              <div
                className="absolute font-display text-sm sm:text-base text-navy"
                style={{
                  left: 0,
                  top: "50%",
                  transform: "translate(calc(-100% - 8px), -50%)",
                }}
              >
                $0
              </div>

              {/* track height holder */}
              <div className="h-4" />
            </div>

            {/* === BELOW BAR: price labels === */}
            {sortedGoals.map((goal) => {
              const pct = Math.min(100, (goal.price / safeMax) * 100);
              const reached = balance >= goal.price;
              return (
                <div
                  key={`bot-${goal.id}`}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: `${pct}%`,
                    transform: "translateX(-50%)",
                    top: "calc(100% - 48px)",
                    width: 90,
                    marginLeft: -45,
                  }}
                >
                  {/* connector tick from bar to price */}
                  <div className="w-1 bg-navy" style={{ height: 8 }} />
                  <div
                    className="font-display text-base sm:text-lg text-navy mt-1"
                    style={{
                      textDecoration: reached ? "line-through" : "none",
                      opacity: reached ? 0.65 : 1,
                    }}
                  >
                    {formatMoney(goal.price)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
